import React, { useState, useContext, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { makeStyles } from "@material-ui/core";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import {
  Tabs,
  Accordion,
  Button,
  Tooltip,
} from "@my-scoot/component-library-legacy";
import {
  SIDEBAR_TO_IFRAME_SECTION_ID,
  SCROLL_TO_SECTION,
} from "@my-scoot/exly-react-component-lib";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import {
  SIDEBAR_KEYS,
  SIDEBAR_SECTIONS,
  TABS,
  DEVICES,
  SIDEBAR_TAB_LABELS,
  SIDEBAR_ACTION_TABS,
  GalleryConfig,
  TestimonialConfig,
  FeaturesConfig,
  StatisticsConfig,
  MiscellaneousConfig,
  OfferingsSectionConfig,
  IntroVideoConfig,
  AboutYouConfig,
  ContactDetailsConfig,
  BlogsConfig,
  TeamConfig,
  AdditionalPointersConfig,
  getTemplateSideBarkeys,
  MANAGE_WEBPAGE_LEARN_MORE_HREFS,
  SECTIONKEY_TO_MISC_KEY,
} from "../../constants";

import { Section } from "./section";
import { WebpageCreationContext } from "../../";
import style from "./style.module.css";
import ListingReordering from "./ListingReordering";
import { isStepsChecked } from "../../utils";
import { hasOutboundIcon } from "./utils";
import { WhitelabelLock } from "features/Account/modules/Whitelabel/modules/WhitelabelLock";
import { FEATURE_KEYS } from "features/Account/Account.constants";
import { useFeatureAccessibility } from "features/Account/utils/useFeatureAccessibility";
import LearnMoreStrip from "features/Common/modules/LearnMore/modules/LearnMoreStrip/LearnMoreStrip";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import { getRAUrlParams } from "utils/urlUtils";
import { SESSION_STORAGE_KEYS } from "features/Common/modules/Storage/modules/SessionStorage/constants/SessionStorage.constant";
import ExlyImage from "common/Components/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { useFetchInitialFormState } from "features/ManageWebpage/modules/Customizations/common/hooks/useFetchInitialFormState";
import { useCustomizationStore } from "features/ManageWebpage/modules/Customizations/store/useCustomizationStore";
import ExlyLoader from "ui/modules/ExlyLoader";
import { useWhitelabelLock } from "features/Account/modules/Whitelabel/modules/WhitelabelLock/useWhitelabelLock";
import { SIDEBAR_HIDDEN_FIELDS_CONFIG } from "./sidebar.constant";

const useStyles = (isMobile) =>
  makeStyles(() => ({
    tabsRoot: {
      display: "flex",
      flexDirection: "row",
      padding: "12px 0px 0 12px",
      position: "sticky",
      top: "0",
      backgroundColor: "#fffffff9",
      backdropFilter: "blur(4px)",
      zIndex: 100,
      boxShadow: "0px 2px 2px 0px #0001",
      "& *": {
        fontFamily: "unset",
      },
    },
    tabItem: {
      padding: isMobile ? "16px 6px" : "0px 0px 8px 0px !important",
      margin: "0 0",
      position: "relative",
      textTransform: "capitalize",
      color: "#272522",
      ...(isMobile && {
        flexGrow: 1,
      }),
    },
    tabFlexContainer: {
      ...(isMobile && {
        justifyContent: "space-between",
      }),
    },

    accordionRoot: {
      margin: "12px!important",
    },
    accordionSummary: {
      margin: "4px 4px",
      "&>div": {
        margin: "0px 0",
      },
    },
    expandedAccordionSummary: {
      margin: "4px 4px",
    },
    sectionHeader: {
      fontSize: 16,
      fontWeight: 600,
    },
    headerTick: {
      color: "#c0c4cc",
      marginRight: "4px",
      width: "24px !important",
      height: "24px !important",
    },
    checked: {
      color: "#089813",
    },
    sectionBody: {
      fontSize: 16,
      padding: isMobile ? "6px 12px 8px" : "8px 16px 12px",
    },
    nextButton: {
      padding: "8px 32px",
    },
  }));

const templateSpecificSectionConfig = {
  [SIDEBAR_KEYS.TESTIMONIALS]: {
    permissionKey: "testimonials",
    enabledTemplatesList: TestimonialConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.GALLERY]: {
    permissionKey: "gallery",
    enabledTemplatesList: GalleryConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.FEATURES]: {
    permissionKey: "features",
    enabledTemplatesList: FeaturesConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.STATISTICS]: {
    permissionKey: "statistics",
    enabledTemplatesList: StatisticsConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.AWARDS]: {
    permissionKey: "awards",
    enabledTemplatesList: MiscellaneousConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.MEDIA_MENTIONS]: {
    permissionKey: "media_mentions",
    enabledTemplatesList: MiscellaneousConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.BRAND_COLLABORATIONS]: {
    permissionKey: "brand_collaborations",
    enabledTemplatesList: MiscellaneousConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.OFFERINGS_SECTION]: {
    permissionKey: "offerings_section",
    enabledTemplatesList: OfferingsSectionConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.INTRO_VIDEO]: {
    permissionKey: "intro_video",
    enabledTemplatesList: IntroVideoConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.ABOUT_YOU]: {
    permissionKey: "about_you",
    enabledTemplatesList: AboutYouConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.CONTACT_DETAILS]: {
    permissionKey: "contact_details",
    enabledTemplatesList: ContactDetailsConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.BLOGS]: {
    permissionKey: "blogs",
    enabledTemplatesList: BlogsConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.TEAMS]: {
    permissionKey: "team",
    enabledTemplatesList: TeamConfig.enableInTemplates,
  },
  [SIDEBAR_KEYS.ADDITIONAL_POINTERS]: {
    permissionKey: "additional_pointers",
    enabledTemplatesList: AdditionalPointersConfig.enableInTemplates,
  },
};

const handleScrollToSection = (sectionKey) => {
  const sectionId = SIDEBAR_TO_IFRAME_SECTION_ID[sectionKey];
  if (!sectionId) return;

  const iframe = document.getElementById("pageFrame");
  if (!iframe?.contentWindow) return;

  iframe.contentWindow.postMessage(
    {
      type: SCROLL_TO_SECTION,
      sectionId,
    },
    "*"
  );
};

const Sidebar = (props) => {
  const searchParams = getRAUrlParams();
  const activeSection = searchParams.get("active_section");
  const isDesktop = useDesktopMediaQuery();
  const muiStyles = useStyles(!isDesktop)();
  const { isFeatureAccessible } = useFeatureAccessibility();

  const {
    handleSave,
    state,
    initialState,
    setState,
    setField,
    errorState,
    setErrorMessage,
    enableError,
    children: footerComponent,
    disableAllEdits,
  } = props;

  const sectionList = Object.values(
    getTemplateSideBarkeys(state?.template?.slug)
  );

  // @dev we are calling this here so that customization form values are set on page load
  const {
    isLoading: isInitialFormStateLoading,
    isFetching: isInitialFormStateFetching,
    refetch: refetchInitialFormState,
  } = useFetchInitialFormState();
  const sectionConfigLoading = useCustomizationStore(
    (state) => state.sectionConfigLoading
  );
  const showInitialFormStateLoader =
    isInitialFormStateLoading ||
    isInitialFormStateFetching ||
    sectionConfigLoading;

  const {
    sidebar_active_tab,
    set_sidebar_active_tab,
    is_settings_view,
    set_is_settings_view,
    device,
    fontList,
    fontListMeta,
    fontSearch,
    templateData,
    miscellaneous_data,

    isSaving,

    subdomain: { checkSubdomainValid },

    testimonials: { testimonialData },
    gallery: { galleryData },
    features: { featuresData },
    statistics: { statisticsData },

    loadMoreFonts,
    isFontListLoading,
    handleFontSearchChange,
  } = useContext(WebpageCreationContext);

  const [openSections, setOpenSection] = useState([SIDEBAR_KEYS.SUBDOMAIN]);

  useEffect(() => {
    if (activeSection && sectionList.includes(activeSection)) {
      setOpenSection([activeSection]);
    }
  }, []);

  const getCurrSectionList = useCallback(() => {
    return sectionList.filter(
      (section) => SIDEBAR_SECTIONS[section].location === sidebar_active_tab
    );
  }, [sidebar_active_tab]);

  // The store outlives tab switches (and edits made in the advanced builder),
  // so re-pull settings every time the settings/reorder view opens instead of
  // showing stale toggles until a window reload.
  useEffect(() => {
    if (sidebar_active_tab === TABS.REORDER && refetchInitialFormState) {
      refetchInitialFormState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar_active_tab]);

  // const [activeTab, setActiveTab] = useState(TABS.MY_PAGE);

  const handleToggle = (sectionProp) => {
    if (
      SIDEBAR_SECTIONS[sectionProp].isWhitelabelFeature &&
      !isFeatureAccessible({ featureKey: FEATURE_KEYS.whitelabel })
    ) {
      return;
    } else {
      setOpenSection(openSections.includes(sectionProp) ? [] : [sectionProp]);
    }
  };

  // Checks if there is a forbid permission flag set for a section
  const showSection = (sectionName) => {
    const permissionObject = JSON.parse(
      window.sessionStorage.getItem(SESSION_STORAGE_KEYS.MENU_PERMISSIONS) ||
        "{}"
    );
    if (
      !("manage_webpage" in permissionObject) ||
      (permissionObject.manage_webpage || []).length === 0
    ) {
      return true;
    }

    return !(permissionObject?.manage_webpage || "").includes(sectionName);
  };

  const handleNextPrompt = async ({ sectionIndex }) => {
    const isSaved = await handleSave();
    if (isSaved !== true) return;

    const currSectionList = getCurrSectionList(sidebar_active_tab);
    if (sectionIndex !== currSectionList.length - 1) {
      const nextSectionKey = currSectionList[sectionIndex + 1];
      // @dev - Checking for locked section
      if (
        SIDEBAR_SECTIONS[nextSectionKey].isWhitelabelFeature &&
        !isFeatureAccessible({ featureKey: FEATURE_KEYS.whitelabel })
      ) {
        return;
      }
      setOpenSection(nextSectionKey);
    }
  };

  const getHiddenFields = ({ sectionKey, template }) => {
    const sectionConfigs = SIDEBAR_HIDDEN_FIELDS_CONFIG[sectionKey] || [];

    return sectionConfigs.flatMap(({ templates, fields }) =>
      templates.includes(template) ? fields : []
    );
  };

  const getSectionProps = ({ sectionKey, template }) => ({
    key: sectionKey,
    sectionKey,
    initialState,
    state: state,
    setState: setState,
    setField: setField,
    errorState: errorState,
    setErrorMessage: setErrorMessage,
    enableError: enableError,
    ...([SIDEBAR_KEYS.COLORS, SIDEBAR_KEYS.COVER].includes(sectionKey) && {
      templateData,
    }),
    disableAllEdits,
    hiddenFields: getHiddenFields({ sectionKey, template }),
    isMobile: !isDesktop,
    other: {
      ...(sectionKey === SIDEBAR_KEYS.LOGO && {
        fontList,
        fontListMeta,
        loadMoreFonts,
        isFontListLoading,
        fontSearch,
        handleFontSearchChange,
        totalFonts: fontListMeta.totalFonts,
      }),
      ...(sectionKey === SIDEBAR_KEYS.SUBDOMAIN && {
        checkSubdomainValid,
      }),
    },
  });

  const getSectionTitle = (sectionKey) => {
    const titleGetter = SIDEBAR_SECTIONS[sectionKey].title;
    if (typeof titleGetter === "function") {
      return titleGetter({ templateName: templateData.slug });
    }
    return (
      <>
        {titleGetter}
        {SIDEBAR_SECTIONS[sectionKey].tooltip ? (
          <Tooltip
            title={SIDEBAR_SECTIONS[sectionKey].tooltip}
            color="primary"
            arrow
          >
            <InfoOutlinedIcon className={style.tooltip} />
          </Tooltip>
        ) : null}
      </>
    );
  };

  const getSectionVisibility = (sectionKey) => {
    const miscKey = SECTIONKEY_TO_MISC_KEY[sectionKey];

    if (!miscKey) return true; // sections without misc data

    const sectionData = miscellaneous_data?.[miscKey];

    if (!Array.isArray(sectionData) || sectionData.length === 0) {
      return true; // safety default
    }

    const visible = sectionData[0]?.data?.visible;

    return visible !== false;
  };

  const hideMainCta = (sectionKey) => {
    return SIDEBAR_SECTIONS[sectionKey].hideMainCta;
  };

  const handleWhitelabelClick = (e, sectionKey) => {
    const isWhitelabelSection =
      SIDEBAR_SECTIONS?.[sectionKey]?.isWhitelabelFeature;

    const hasAccess = isFeatureAccessible({
      featureKey: FEATURE_KEYS.whitelabel,
    });

    if (isWhitelabelSection && !hasAccess) {
      toggleWhitelabelUnlockModal(e);
    }
  };

  const isTitleChecked = isStepsChecked(
    initialState,
    SIDEBAR_KEYS,
    testimonialData,
    galleryData,
    featuresData,
    miscellaneous_data,
    statisticsData
  )[0];

  const { toggleWhitelabelUnlockModal } = useWhitelabelLock();

  return (
    <div
      style={sidebar_active_tab === TABS.REORDER ? { width: "100%" } : {}}
      className={classnames(
        style.sidebar,
        style[`sidebar-${sidebar_active_tab.toLowerCase()}`]
      )}
      id="webpageBuilder-sidebar-root"
    >
      {(() => {
        const tabParam = getRAUrlParams().get("tab");
        if (tabParam && TABS[tabParam]) return null;
        if (is_settings_view) return null;
        const allTabs = Object.entries(SIDEBAR_TAB_LABELS)
          .filter(([k]) => k !== TABS.REORDER)
          .map(([k, v]) => ({
            label: v,
            value: k,
          }));

        const isBuilderMobile = !isDesktop || device === DEVICES.MOBILE;

        const tabs = isBuilderMobile
          ? allTabs.flatMap((tab) => {
              if (tab.value === TABS.APPEARANCE) {
                return [
                  tab,
                  {
                    label: "Settings",
                    value: SIDEBAR_ACTION_TABS.SETTINGS,
                  },
                ];
              }
              return [tab];
            })
          : allTabs;

        return (
          <Tabs
            tabs={tabs}
            value={sidebar_active_tab}
            onChange={(e, newTab) => {
              if (newTab === SIDEBAR_ACTION_TABS.SETTINGS) {
                set_is_settings_view(true);
                set_sidebar_active_tab(TABS.REORDER);
                return;
              }
              set_sidebar_active_tab(newTab);
            }}
            classes={{
              root: muiStyles.tabsRoot,
              flexContainer: muiStyles.tabFlexContainer,
            }}
            tab_classes={{
              root: muiStyles.tabItem,
              selected: muiStyles.selectedTabItem,
            }}
          />
        );
      })()}

      <LearnMoreStrip
        about="about managing your webpage"
        href={MANAGE_WEBPAGE_LEARN_MORE_HREFS.MANAGE_WEBPAGE}
        className={style.learnMoreStrip}
      />

      {showInitialFormStateLoader ? (
        <ExlyLoader className="tw-h-[calc(100vh_-_var(--navbar-height,0)_-_110px)]" />
      ) : (
        <>
          {sidebar_active_tab === TABS.REORDER ? (
            <div className={style.appearanceContainer}>
              <ListingReordering disableAllEdits={disableAllEdits} />
            </div>
          ) : (
            <div className={style.sectionContainer}>
              {getCurrSectionList(sidebar_active_tab)
                .filter((sKey) => {
                  if (sKey in templateSpecificSectionConfig) {
                    const sectionConfig = templateSpecificSectionConfig[sKey];

                    return (
                      sectionConfig.enabledTemplatesList.includes(
                        state?.template?.slug
                      ) && showSection(sectionConfig.permissionKey)
                    );
                  }

                  return true;
                })
                .map((sectionKey, sectionIndex) => {
                  const isVisible = getSectionVisibility(sectionKey);

                  const shouldShowOutboundIcon = hasOutboundIcon({
                    sectionKey,
                    isTitleChecked,
                    isVisible: isVisible,
                  });

                  return (
                    <Accordion
                      expanded={openSections.includes(sectionKey)}
                      key={sectionKey}
                      expandIconPosition="end"
                      className={muiStyles.accordionRoot}
                      summaryClassName={muiStyles.accordionSummary}
                      expandedClassName={muiStyles.expandedAccordionSummary}
                      onChange={() => handleToggle(sectionKey)}
                      TransitionProps={{ unmountOnExit: true }}
                      accordionSummaryContent={
                        <div
                          className={classnames(
                            "d-flex align-items-center justify-content-between w-100 tw-transition-opacity tw-duration-200",
                            getSectionVisibility?.(sectionKey) === false &&
                              "tw-opacity-50"
                          )}
                          onClick={(e) =>
                            handleWhitelabelClick?.(e, sectionKey)
                          }
                        >
                          <div className={`${muiStyles.sectionHeader}`}>
                            <CheckCircleIcon
                              className={classnames(
                                muiStyles.headerTick,
                                isVisible &&
                                  isTitleChecked?.[sectionKey] &&
                                  muiStyles.checked
                              )}
                            />
                            {getSectionTitle(sectionKey)}
                          </div>

                          <div className="tw-hidden lg:tw-block">
                            {shouldShowOutboundIcon && (
                              <ExlyImage
                                src={getS3AssetCDNUrl(
                                  "assets/vectors/common/outbound.svg"
                                )}
                                width={20}
                                height={20}
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleScrollToSection(sectionKey);
                                }}
                              />
                            )}
                          </div>

                          {SIDEBAR_SECTIONS[sectionKey].isWhitelabelFeature && (
                            <WhitelabelLock
                              iconStyle={{
                                fill: "rgba(51, 34, 0, 1)",
                                fontSize: 12,
                              }}
                              className={style.WhitelabelLockContainer}
                              modalDescription={{
                                heading:
                                  "Edit Footer Section Details via White-labelling",
                              }}
                            />
                          )}
                        </div>
                      }
                    >
                      <div className={muiStyles.sectionBody}>
                        <Section
                          {...getSectionProps({
                            sectionKey,
                            template: state?.template?.slug,
                          })}
                        />
                        {!hideMainCta(sectionKey) && (
                          <div className={style.nextSectionPrompt}>
                            <Button
                              variant="contained"
                              color="primary"
                              disableElevation
                              className={muiStyles.nextButton}
                              disabled={isSaving || disableAllEdits}
                              loading={isSaving}
                              onClick={() =>
                                handleNextPrompt({
                                  sectionIndex,
                                  sectionKey,
                                })
                              }
                            >
                              Save
                            </Button>
                          </div>
                        )}
                      </div>
                    </Accordion>
                  );
                })}
            </div>
          )}
        </>
      )}

      {isDesktop && sidebar_active_tab === TABS.REORDER
        ? null
        : footerComponent}
    </div>
  );
};

Sidebar.propTypes = {
  handleSave: PropTypes.func,
  state: PropTypes.object,
  initialState: PropTypes.object,
  setState: PropTypes.func,
  setField: PropTypes.func,
  fontList: PropTypes.array,
  errorState: PropTypes.object,
  setErrorMessage: PropTypes.func,
  enableError: PropTypes.func,
  other: PropTypes.shape({
    updateSubdomain: PropTypes.func,
    checkSubdomainValid: PropTypes.func,

    testimonialData: PropTypes.array,
    setTestimonialData: PropTypes.func,
  }),
};

export { Section, Sidebar };
